import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Card, CardBody, Col, Row } from 'reactstrap';
import {
  getCampaignHasFeatureEnabled,
  PERFORMANCE_FEATURE_HIDE_RATING_COMMENTS,
  PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER,
} from 'utils/models/PerformanceUtils';
import { Person, Relationship } from '../../../../types';
import { getCampaignRatings } from '../../../../utils/models/Performance';
import { CycleDiffBadge } from '../../components/CycleDiffBadge';
import { PrivateDataBadge } from '../../components/PrivateDataBadge';
import PerformanceWrittenResponses from '../PerformanceWrittenResponses';
import PersonPerformanceContext from '../PersonPerformanceContext';
import { FormattedResponse } from '../utils';
import { ManagerComments } from './ManagerComments';
import PromotionNominationInfo from './PromotionNominationInfo';
import { compareRatingScales } from './TrajectoryUtils';

interface ManagerRatingCardProps {
  person?: {
    id: number;
    given_name: string;
  };
  isInReviewFlow: boolean;
  hideRatingFromDirectReport: boolean;
  hideRatingCommentsFromDirectReport: boolean;
  showManagerOnlyPerformanceDetails: boolean;
  managerRelationshipsWithFeedback?: Relationship[];
  previousRatingDifference?: number;
  manager: Person;
  isAdditionalManager?: boolean;
  ratingSectionWrittenResponses?: FormattedResponse[];
}

const ManagerRatingCard: React.FC<ManagerRatingCardProps> = ({
  isInReviewFlow,
  hideRatingFromDirectReport,
  hideRatingCommentsFromDirectReport,
  showManagerOnlyPerformanceDetails,
  managerRelationshipsWithFeedback,
  manager,
  isAdditionalManager = false,
  ratingSectionWrittenResponses,
}) => {
  const { formatMessage } = useIntl();
  const personPerformanceContext = useContext(PersonPerformanceContext);

  const selectedCampaign = personPerformanceContext.selectedCampaignData;

  const previousCampaignWithRating =
    personPerformanceContext.previousCampaignDataWithRating;

  const latestRatingValue = isAdditionalManager
    ? (managerRelationshipsWithFeedback?.[0]?.final_rating ??
      managerRelationshipsWithFeedback?.[0]?.rating)
    : selectedCampaign?.rating?.value;

  const selectedCampaignRatingScale = useMemo(
    () =>
      getCampaignRatings(selectedCampaign?.campaign).sort(
        (a, b) => b.value - a.value
      ),
    [selectedCampaign]
  );

  const latestRatingDefinition = useMemo(
    () =>
      selectedCampaignRatingScale?.find((r) => r.value === latestRatingValue),
    [selectedCampaignRatingScale, latestRatingValue]
  );

  const latestRatingName = latestRatingDefinition?.name;

  const latestRatingDescription = useMemo(() => {
    const ratingDescription =
      latestRatingDefinition?.description ||
      formatMessage({
        id: 'app.views.person.person_performance.trajectory.summary.no_rating_description',
        defaultMessage:
          'Your manager has not provided you with a rating for this cycle.',
      });

    return ratingDescription;
  }, [formatMessage, latestRatingDefinition?.description]);

  const previousRatingDifference = useMemo(() => {
    if (!previousCampaignWithRating) return null;
    // if the person is not eligible in one of the two campaigns,
    // there is no point in calculating a difference
    if (
      !previousCampaignWithRating?.is_eligible_for_reporting ||
      !selectedCampaign?.is_eligible_for_reporting
    ) {
      return null;
    }

    // if the person didn't receive a rating, don't compute the difference
    // == on purpose here, I want to check null or undefined
    if (
      selectedCampaign?.rating?.value == null ||
      previousCampaignWithRating?.rating?.value == null
    ) {
      return null;
    }

    const previousCampaignRatingScale = getCampaignRatings(
      previousCampaignWithRating?.campaign
    ).sort((a, b) => b.value - a.value);

    if (
      !previousCampaignRatingScale ||
      !compareRatingScales(
        previousCampaignRatingScale,
        selectedCampaignRatingScale
      )
    )
      return null;

    const prevRating = previousCampaignWithRating?.rating?.value;
    return (latestRatingValue ?? 0) - prevRating;
  }, [
    latestRatingValue,
    previousCampaignWithRating,
    selectedCampaign,
    selectedCampaignRatingScale,
  ]);

  const hasPromotionNomination =
    !isAdditionalManager &&
    showManagerOnlyPerformanceDetails &&
    getCampaignHasFeatureEnabled(
      selectedCampaign?.campaign,
      PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER
    );

  const ratingCommentsHidden = getCampaignHasFeatureEnabled(
    selectedCampaign?.campaign,
    PERFORMANCE_FEATURE_HIDE_RATING_COMMENTS
  );

  const recommendForPromotion =
    managerRelationshipsWithFeedback?.[0]?.recommend_for_promotion;

  const promotionComments =
    managerRelationshipsWithFeedback?.[0]?.promotion_comments;

  return (
    <Card className="rating-card">
      <CardBody>
        {/* latest-rating */}
        {!(isInReviewFlow && !isAdditionalManager) && (
          <Row className="mb-4 align-items-start">
            <Col>
              <h6 className="text-secondary text-uppercase">
                <FormattedMessage
                  id="app.views.person.person_performance.trajectory.trajectory_summary.manager_rating"
                  defaultMessage="Manager rating"
                />{' '}
                {hideRatingFromDirectReport && <PrivateDataBadge />}
              </h6>
              <h2 className="mb-3">
                {latestRatingName || (
                  <span className="fst-italic">
                    <FormattedMessage
                      id="app.views.person.person_performance.trajectory.trajectory_summary.no_rating_provided"
                      defaultMessage="No rating provided"
                    />
                  </span>
                )}
                {!!showManagerOnlyPerformanceDetails &&
                  personPerformanceContext.canDisplayDeltas &&
                  !isAdditionalManager && (
                    // @ts-expect-error
                    <CycleDiffBadge value={previousRatingDifference} />
                  )}
              </h2>
            </Col>
            {hasPromotionNomination && recommendForPromotion != null && (
              <PromotionNominationInfo
                recommendForPromotion={recommendForPromotion}
                promotionComments={promotionComments}
                campaign={selectedCampaign?.campaign}
                promotionPacket={personPerformanceContext.promotionPacket}
              />
            )}
          </Row>
        )}
        {/* manager-comments */}
        {!ratingCommentsHidden &&
        managerRelationshipsWithFeedback &&
        managerRelationshipsWithFeedback.length > 0 &&
        (showManagerOnlyPerformanceDetails ||
          !hideRatingCommentsFromDirectReport) ? (
          <ManagerComments
            isPrivate={hideRatingCommentsFromDirectReport}
            author={manager}
            comments={managerRelationshipsWithFeedback[0]?.rating_comments}
          />
        ) : (
          <>
            <h6 className="text-uppercase text-secondary">
              <FormattedMessage
                id="app.views.person.person_performance.trajectory.trajectory_summary.rating_description"
                defaultMessage="Rating description"
              />
            </h6>
            <p className="text-muted mb-0">{latestRatingDescription}</p>
          </>
        )}
        {ratingSectionWrittenResponses &&
          ratingSectionWrittenResponses.length > 0 && (
            <div className="mt-4">
              <h6 className="text-uppercase text-secondary mb-3">
                <FormattedMessage
                  id="app.views.person.person_performance.trajectory.manager_rating_card.additional_rating_feedback"
                  defaultMessage="Additional rating feedback"
                />
              </h6>
              <PerformanceWrittenResponses
                person={manager}
                // @ts-expect-error - FormattedResponse[] / WrittenResponse[] divergence (pre-existing)
                questions={ratingSectionWrittenResponses}
              />
            </div>
          )}
      </CardBody>
    </Card>
  );
};

export default ManagerRatingCard;
